/**
 * Office.js helpers for reading/modifying the selection, the document body,
 * and the document's change-tracking state.
 */

/* global Word, console */

import { ApplyOperation } from '../services/analysis';
import { ReviewEdit } from '../services/review';

/**
 * Word on the web throws a bare `GeneralException` from `Section.getHeader` on some
 * documents (the API is declared in WordApi 1.1, but the web host can still refuse
 * it depending on how the section is set up). Headers and footers are supplementary
 * context for the full review — they must never take the whole operation down with
 * them — so probe the capability once and skip them for the rest of the session.
 *
 * The probe lives outside `Word.run` on purpose: a sync that rejects mid-batch
 * leaves the surrounding context in an unreliable state, so callers resolve this
 * flag *before* opening the context they actually intend to use.
 */
let headerFooterProbe: Promise<boolean> | null = null;

export function canReadHeadersFooters(): Promise<boolean> {
  if (!headerFooterProbe) {
    headerFooterProbe = Word.run(async context => {
      const sections = context.document.sections;
      sections.load('items');
      await context.sync();
      if (sections.items.length === 0) return false;

      const header = sections.items[0].getHeader(Word.HeaderFooterType.primary);
      header.load('text');
      await context.sync();
      return true;
    }).catch(e => {
      console.log('[word] headers/footers unreadable — continuing with body only', e);
      return false;
    });
  }
  return headerFooterProbe;
}

/** Collects primary header + footer text. Only call when `canReadHeadersFooters()` is true. */
async function readHeaderFooterText(): Promise<string[]> {
  return Word.run(async context => {
    const sections = context.document.sections;
    sections.load('items');
    await context.sync();

    const bodies = sections.items.flatMap(section => [
      section.getHeader(Word.HeaderFooterType.primary),
      section.getFooter(Word.HeaderFooterType.primary),
    ]);
    bodies.forEach(b => b.load('text'));
    await context.sync();

    return bodies.map(b => b.text ?? '');
  }).catch(e => {
    console.log('[word] failed to read headers/footers', e);
    return [] as string[];
  });
}

export async function getSelectedText(): Promise<string> {
  return Word.run(async context => {
    const range = context.document.getSelection();
    range.load('text');
    await context.sync();
    return range.text ?? '';
  });
}

/**
 * Apply `text` to the current selection using the requested operation.
 * On a collapsed selection (cursor only) every operation degrades to an
 * insertion at the cursor — a safety fallback if the user's selection
 * changed between generating the suggestion and applying it.
 */
export async function applyToSelection(
  text: string,
  operation: ApplyOperation = 'replace',
): Promise<void> {
  const location =
    operation === 'insert_before'
      ? Word.InsertLocation.before
      : operation === 'insert_after'
        ? Word.InsertLocation.after
        : Word.InsertLocation.replace;

  await Word.run(async context => {
    const range = context.document.getSelection();
    range.insertText(text, location);
    await context.sync();
  });
}

/** Back-compat alias for "replace" semantics. */
export async function replaceSelectedText(newText: string): Promise<void> {
  await applyToSelection(newText, 'replace');
}

export async function getDocumentText(): Promise<string> {
  return Word.run(async context => {
    const body = context.document.body;
    body.load('text');
    await context.sync();
    return body.text ?? '';
  });
}

export async function getFullReviewDocumentText(): Promise<string> {
  const bodyText = await getDocumentText();
  if (!(await canReadHeadersFooters())) return bodyText;

  const headerFooterText = await readHeaderFooterText();
  return [bodyText, ...headerFooterText].filter(Boolean).join('\n');
}

export async function selectByText(snippet: string): Promise<boolean> {
  const trimmed = snippet.trim();
  if (!trimmed) return false;
  const searchText = trimmed.length > 200 ? trimmed.slice(0, 200) : trimmed;
  return Word.run(async context => {
    const results = context.document.body.search(searchText, { matchCase: false });
    results.load('items');
    await context.sync();
    if (results.items.length === 0) return false;
    results.items[0].select();
    await context.sync();
    return true;
  });
}

export async function isTrackChangesOn(): Promise<boolean> {
  return Word.run(async context => {
    context.document.load('changeTrackingMode');
    await context.sync();
    return context.document.changeTrackingMode !== Word.ChangeTrackingMode.off;
  });
}

export async function enableTrackChanges(): Promise<void> {
  await Word.run(async context => {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
    await context.sync();
  });
}

// ---------------------------------------------------------------------------
// Agreement insertion (Drafting tab)
// ---------------------------------------------------------------------------

export async function insertDocxBlob(blob: Blob): Promise<void> {
  const arrayBuffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  const CHUNK = 8192;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  const base64 = btoa(binary);

  await Word.run(async context => {
    context.document.insertFileFromBase64(base64, Word.InsertLocation.replace);
    await context.sync();
  });
}

/**
 * Inserts a generated agreement (a full .docx from signus-service, base64-
 * encoded) into the current document. `location` is caller-chosen — unlike
 * TemplatesTab's "start a new document from this template" flow (always
 * `replace`), drafting a clause could reasonably go either way, so the
 * DraftingTab prompts the user rather than assuming.
 */
export async function insertGeneratedAgreement(
  contentBase64: string,
  location: 'end' | 'replace' = 'end',
): Promise<void> {
  await Word.run(async context => {
    const insertLocation =
      location === 'replace' ? Word.InsertLocation.replace : Word.InsertLocation.end;
    context.document.insertFileFromBase64(contentBase64, insertLocation);
    await context.sync();
  });
}

// ---------------------------------------------------------------------------
// Full-document redline review (Analyze tab)
// ---------------------------------------------------------------------------
export type SkipReason = 'hallucinated' | 'not_found' | 'error';

export interface SkippedEdit {
  snippet: string;
  reason: SkipReason;
  detail?: string;
}

export interface ApplyEditResult {
  applied: number;
  skipped: number;
  total: number;
  /** Per-type counts of successfully applied edits. */
  breakdown: { replace: number; insert_after: number; insert_before: number; delete: number };
  skips: SkippedEdit[];
}

function snippetFor(find: string): string {
  const trimmed = find.trim();
  return trimmed.length > 100 ? `${trimmed.slice(0, 100)}…` : trimmed;
}

function normalizeAnchorText(text: string): string {
  return text
    .replace(/[‘’‚‛]/g, "'")
    .replace(/[“”„‟]/g, '"')
    .replace(/[–—−]/g, '-')
    .replace(/[\u00A0\u2000-\u200A\u202F\u2060]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function splitAnchorSegments(find: string): string[] {
  return find
    .split(/\r\n|\r|\n/)
    .map(s => s.trim())
    .filter(Boolean);
}

function stripListPrefix(text: string): string {
  return text.replace(/^\(?[0-9a-zA-Z]+(?:\.[0-9a-zA-Z]+)*[.)]\s+/, '');
}

function matchesBySegment(find: string, normalizedDocText: string): boolean {
  const segments = splitAnchorSegments(find);
  if (segments.length < 2) return false;
  return segments.every(seg => {
    const normSeg = normalizeAnchorText(seg);
    if (normalizedDocText.includes(normSeg)) return true;
    const stripped = stripListPrefix(normSeg);
    return stripped !== normSeg && normalizedDocText.includes(stripped);
  });
}

/**
 * Sort edits by their position in the source document, descending. Applying
 * bottom-up means earlier edits don't shift the offsets of later (i.e. earlier
 * in the doc) edits — each subsequent search still finds its anchor.
 *
 * Edits whose `find` doesn't appear verbatim in `docText` are filtered out
 * upstream. We use indexOf rather than lastIndexOf because the prompt requires
 * unique anchors; the first occurrence is the intended one.
 */
function sortEditsBottomUp(edits: ReviewEdit[], docText: string): ReviewEdit[] {
  return [...edits].sort((a, b) => docText.indexOf(b.find) - docText.indexOf(a.find));
}

/**
 * Pre-flight verbatim verification — drops edits whose anchor doesn't literally
 * exist in the document text we sent to the model. Catches hallucinated snippets
 * before we attempt to apply them. An anchor that only matches after
 * `normalizeAnchorText` (e.g. the model saw curly quotes where the docx has
 * straight ones, or vice versa) is still accepted here — `runOneEdit` retries
 * with the same normalization when the live Word search comes up empty. An
 * anchor that only matches line-by-line (`matchesBySegment`) is accepted too —
 * see `splitAnchorSegments` for why a paragraph-spanning anchor can genuinely
 * exist in the document without appearing as one contiguous run.
 */
function verifyEdits(
  edits: ReviewEdit[],
  docText: string,
): {
  valid: ReviewEdit[];
  invalid: ReviewEdit[];
} {
  const normalizedDocText = normalizeAnchorText(docText);
  const valid: ReviewEdit[] = [];
  const invalid: ReviewEdit[] = [];
  for (const e of edits) {
    const longEnough = !!e.find && e.find.length >= 20;
    const verbatim = longEnough && docText.includes(e.find);
    const normalizedMatch = longEnough && normalizedDocText.includes(normalizeAnchorText(e.find));
    const segmentMatch =
      longEnough && !verbatim && !normalizedMatch && matchesBySegment(e.find, normalizedDocText);
    if (verbatim || normalizedMatch || segmentMatch) {
      valid.push(e);
    } else {
      invalid.push(e);
    }
  }
  return { valid, invalid };
}

async function findAnchorRange(
  context: Word.RequestContext,
  find: string,
  includeHeadersFooters: boolean,
): Promise<Word.Range | null> {
  const truncate = (text: string) => (text.length > 200 ? text.slice(0, 200) : text);
  const candidates = [...new Set([truncate(find), truncate(normalizeAnchorText(find))])];

  const bodySearches = candidates.map(candidate =>
    context.document.body.search(candidate, { matchCase: false }),
  );
  bodySearches.forEach(results => results.load('items'));
  await context.sync();

  for (const results of bodySearches) {
    if (results.items.length > 0) return results.items[0];
  }

  if (!includeHeadersFooters) return findSegmentBridgedRange(context, find);

  const sections = context.document.sections;
  sections.load('items');
  await context.sync();

  if (sections.items.length > 0) {
    const headerFooterBodies = sections.items.flatMap(section => [
      section.getHeader(Word.HeaderFooterType.primary),
      section.getFooter(Word.HeaderFooterType.primary),
    ]);
    const hfSearches = headerFooterBodies.flatMap(body =>
      candidates.map(candidate => body.search(candidate, { matchCase: false })),
    );
    hfSearches.forEach(results => results.load('items'));
    await context.sync();

    for (const results of hfSearches) {
      if (results.items.length > 0) return results.items[0];
    }
  }

  return findSegmentBridgedRange(context, find);
}

async function findSegmentBridgedRange(
  context: Word.RequestContext,
  find: string,
): Promise<Word.Range | null> {
  const segments = splitAnchorSegments(find);
  if (segments.length < 2) return null;

  const lastSegment = segments[segments.length - 1];
  const lastCandidates = [...new Set([lastSegment, normalizeAnchorText(lastSegment)])].filter(
    c => c.length >= 20,
  );
  if (lastCandidates.length === 0) return null;

  const lastSearches = lastCandidates.map(c =>
    context.document.body.search(c, { matchCase: false }),
  );
  lastSearches.forEach(results => results.load('items'));
  await context.sync();

  const lastRange = lastSearches.map(results => results.items[0]).find(Boolean);
  if (!lastRange) return null;

  const lastParagraphs = lastRange.paragraphs;
  lastParagraphs.load('items');
  await context.sync();
  let paragraph = lastParagraphs.items[0];
  if (!paragraph) return null;

  for (let i = segments.length - 2; i >= 0; i--) {
    const previous = paragraph.getPreviousOrNullObject();
    previous.load('text,isNullObject');
    await context.sync();
    if (previous.isNullObject) return null;

    const expected = normalizeAnchorText(segments[i]);
    const actual = normalizeAnchorText(previous.text ?? '');
    const matches =
      actual === expected ||
      actual === stripListPrefix(expected) ||
      stripListPrefix(actual) === expected;
    if (!matches) return null;

    paragraph = previous;
  }

  return paragraph.getRange().expandTo(lastRange);
}

interface RunEditResult {
  ok: boolean;
  reason?: 'not_found';
}

async function runOneEdit(edit: ReviewEdit): Promise<RunEditResult> {
  // Resolved before opening the context — see `canReadHeadersFooters`.
  const includeHeadersFooters = await canReadHeadersFooters();

  return Word.run(async (context): Promise<RunEditResult> => {
    const range = await findAnchorRange(context, edit.find, includeHeadersFooters);
    if (!range) {
      console.log('[review] anchor not found in body or headers/footers:', snippetFor(edit.find));
      return { ok: false, reason: 'not_found' };
    }

    switch (edit.type) {
      case 'replace':
        range.insertText(edit.replace_with ?? '', Word.InsertLocation.replace);
        break;
      case 'insert_after':
        range.insertText(edit.text ?? '', Word.InsertLocation.after);
        break;
      case 'insert_before':
        range.insertText(edit.text ?? '', Word.InsertLocation.before);
        break;
      case 'delete':
        range.delete();
        break;
    }
    await context.sync();
    return { ok: true };
  });
}

/**
 * Apply a list of redline edits to the document, with progress callbacks.
 * Caller is responsible for ensuring Track Changes is on before calling so
 * the edits land as visible redlines rather than silent overwrites.
 */
export async function applyReviewEdits(
  edits: ReviewEdit[],
  documentText: string,
  onProgress?: (current: number, total: number) => void,
): Promise<ApplyEditResult> {
  const { valid, invalid } = verifyEdits(edits, documentText);
  const sorted = sortEditsBottomUp(valid, documentText);

  const breakdown = { replace: 0, insert_after: 0, insert_before: 0, delete: 0 };
  const skips: SkippedEdit[] = invalid.map(e => ({
    snippet: snippetFor(e.find),
    reason: 'hallucinated',
  }));
  let applied = 0;

  for (let i = 0; i < sorted.length; i++) {
    if (onProgress) onProgress(i + 1, sorted.length);
    const edit = sorted[i];
    try {
      const result = await runOneEdit(edit);
      if (result.ok) {
        applied++;
        breakdown[edit.type] += 1;
      } else {
        skips.push({ snippet: snippetFor(edit.find), reason: result.reason ?? 'not_found' });
      }
    } catch (e) {
      skips.push({
        snippet: snippetFor(edit.find),
        reason: 'error',
        detail: e instanceof Error ? e.message : String(e),
      });
    }
  }

  return {
    applied,
    skipped: skips.length,
    total: edits.length,
    breakdown,
    skips,
  };
}
